import { Extension } from "@tiptap/core";
import {
  EDITOR_INDENTABLE_NODES,
  EDITOR_INDENT_STEP,
  EDITOR_LIFT_LIST_ITEM_COMMAND,
  EDITOR_LIST_ITEM_NODE_TYPE,
  EDITOR_MAX_INDENT,
  EDITOR_SINK_LIST_ITEM_COMMAND
} from "../../../utils/constants/appConstants";

const clampIndent = (value) => Math.min(Math.max(value, 0), EDITOR_MAX_INDENT);

/**
 * Inside a list, indenting means nesting the item — that is what a writer
 * expects from the same button, so delegate to the list commands there and only
 * fall back to block padding for standalone blocks.
 */
const shiftIndent =
  (direction) =>
  ({ state, dispatch, editor }) => {
    if (editor.isActive(EDITOR_LIST_ITEM_NODE_TYPE)) {
      const listItemCommand =
        direction > 0
          ? EDITOR_SINK_LIST_ITEM_COMMAND
          : EDITOR_LIFT_LIST_ITEM_COMMAND;
      return editor.commands[listItemCommand](EDITOR_LIST_ITEM_NODE_TYPE);
    }

    const { from, to } = state.selection;
    const { tr } = state;
    let changed = false;

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (!EDITOR_INDENTABLE_NODES.includes(node.type.name)) return true;

      const indent = clampIndent(
        node.attrs.indent + direction * EDITOR_INDENT_STEP
      );
      if (indent !== node.attrs.indent) {
        // setNodeMarkup leaves the document size untouched, so positions
        // gathered by this walk stay valid for the rest of it.
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent });
        changed = true;
      }
      return false;
    });

    if (changed && dispatch) dispatch(tr.scrollIntoView());
    return changed;
  };

/**
 * Indentation is stored as `padding-left` in EDITOR_INDENT_STEP-sized steps
 * because that is the shape the notes already in the database carry, so they
 * re-open unchanged.
 */
export const Indent = Extension.create({
  name: "indent",

  addGlobalAttributes() {
    return [
      {
        types: EDITOR_INDENTABLE_NODES,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) =>
              clampIndent(parseInt(element.style.paddingLeft, 10) || 0),
            renderHTML: ({ indent }) =>
              indent ? { style: `padding-left: ${indent}px` } : {}
          }
        }
      }
    ];
  },

  addCommands() {
    return {
      indent: () => shiftIndent(1),
      outdent: () => shiftIndent(-1)
    };
  }
});

export default Indent;
